import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, Send, AlertTriangle, CalendarDays, CalendarClock,
  Zap, Pause, Play, Eye, Trash2, X, ChevronRight, Timer, CheckCircle2,
  FileText, Image, Video
} from "lucide-react";
import { useTranslation } from "../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ICONS = { paid: Clock, paused: Pause, sent: Send, delivery_failed: AlertTriangle };
const STATUS_COLORS = {
  paid: "bg-blue-100 text-blue-700 border-blue-200",
  paused: "bg-orange-100 text-orange-700 border-orange-200",
  sent: "bg-emerald-100 text-emerald-700 border-emerald-200",
  delivery_failed: "bg-red-100 text-red-700 border-red-200",
};

function StatusBadge({ status }) {
  const { t } = useTranslation();
  const Icon = STATUS_ICONS[status] || Clock;
  const color = STATUS_COLORS[status] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${color}`}>
      <Icon className="w-3 h-3" />{t(`status.${status}`) || status}
    </span>
  );
}

function formatDateTime(dateStr, lang) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function timeUntil(dateStr, t, lang) {
  if (!dateStr) return "";
  const diff = new Date(dateStr) - new Date();
  if (diff < 0) {
    const ago = Math.abs(diff);
    if (ago < 3600000) return `${t("scheduled.timeAgo")} ${Math.floor(ago / 60000)} ${t("scheduled.min")}`;
    if (ago < 86400000) return `${t("scheduled.timeAgo")} ${Math.floor(ago / 3600000)}h`;
    return `${t("scheduled.timeAgo")} ${Math.floor(ago / 86400000)}${lang === "fr" ? "j" : "d"}`;
  }
  if (diff < 3600000) return `${t("scheduled.timeIn")} ${Math.floor(diff / 60000)} ${t("scheduled.min")}`;
  if (diff < 86400000) return `${t("scheduled.timeIn")} ${Math.floor(diff / 3600000)}h`;
  return `${t("scheduled.timeIn")} ${Math.floor(diff / 86400000)}${lang === "fr" ? "j" : "d"}`;
}

function SummaryCard({ icon: Icon, label, count, color, accent }) {
  return (
    <div className={`flex items-center gap-3 p-4 rounded-xl border ${color}`} data-testid={`summary-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`p-2.5 rounded-lg ${accent}`}><Icon className="w-5 h-5 text-white" /></div>
      <div>
        <p className="text-2xl font-bold text-stone-900">{count}</p>
        <p className="text-xs text-stone-500 font-medium">{label}</p>
      </div>
    </div>
  );
}

function MessageModal({ message, onClose, onAction, t, lang }) {
  if (!message) return null;
  const fileIcon = (name) => {
    if (!name) return FileText;
    const ext = name.split(".").pop()?.toLowerCase();
    if (["mp4", "webm", "mov"].includes(ext)) return Video;
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return Image;
    return FileText;
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="scheduled-message-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-stone-100 p-5 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{t("scheduled.detailTitle")}</h2>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition-colors" data-testid="modal-close-btn"><X className="w-5 h-5 text-stone-500" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="flex flex-wrap gap-3 items-center">
            <StatusBadge status={message.status} />
            <span className="text-xs text-stone-400">{timeUntil(message.delivery_date, t, lang)}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">{t("scheduled.senderLabel")}</p>
              <p className="font-semibold text-stone-800">{message.sender_name}</p>
              <p className="text-sm text-stone-500">{message.sender_email}</p>
            </div>
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">{t("scheduled.recipientLabel")}</p>
              <p className="font-semibold text-stone-800">{message.recipient_name}</p>
              <p className="text-sm text-stone-500">{message.recipient_email}</p>
            </div>
          </div>
          <div className="bg-stone-50 rounded-xl p-4">
            <p className="text-xs text-stone-400 uppercase tracking-wide mb-1">{t("scheduled.deliveryExpected")}</p>
            <p className="font-semibold text-stone-800">{formatDateTime(message.delivery_date, lang)}</p>
          </div>
          {message.title && (<div><p className="text-xs text-stone-400 uppercase tracking-wide mb-1">{t("admin.titleCol")}</p><p className="font-semibold text-stone-800 text-lg">{message.title}</p></div>)}
          <div>
            <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">{t("scheduled.content")}</p>
            <div className="bg-[#FDF0F4] rounded-xl p-4 text-stone-700 leading-relaxed whitespace-pre-wrap">
              {message.content || <span className="italic text-stone-400">{t("admin.noText")}</span>}
            </div>
          </div>
          {message.files && message.files.length > 0 && (
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wide mb-2">{t("scheduled.files")} ({message.files.length})</p>
              <div className="space-y-2">
                {message.files.map((f, i) => {
                  const FIcon = fileIcon(f.file_name || f.original_name);
                  return (<div key={i} className="flex items-center gap-3 bg-stone-50 rounded-lg p-3"><FIcon className="w-5 h-5 text-[#E8175D]" /><p className="text-sm font-medium text-stone-700 truncate">{f.original_name || f.file_name || `File ${i + 1}`}</p></div>);
                })}
              </div>
            </div>
          )}
          {message.status !== "sent" && (
            <div className="flex flex-wrap gap-2 pt-3 border-t border-stone-100">
              <button onClick={() => { onAction("force-send", message.message_id); onClose(); }} className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors" data-testid="modal-force-send-btn"><Zap className="w-4 h-4" /> {t("admin.forceSend")}</button>
              <button onClick={() => { onAction("pause", message.message_id); onClose(); }} className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors" data-testid="modal-pause-btn">
                {message.status === "paused" ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                {message.status === "paused" ? t("admin.resume") : t("admin.pause")}
              </button>
              <button onClick={() => { onAction("delete", message.message_id); onClose(); }} className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors ml-auto" data-testid="modal-delete-btn"><Trash2 className="w-4 h-4" /> {t("common.delete")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, description, confirmLabel, confirmColor, onConfirm, onClose, t }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="confirm-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-stone-900 mb-2">{title}</h3>
        <p className="text-sm text-stone-500 mb-5">{description}</p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-stone-200 rounded-lg text-stone-600 hover:bg-stone-50 transition-colors text-sm font-medium">{t("admin.cancel")}</button>
          <button onClick={onConfirm} className={`flex-1 px-4 py-2.5 text-white rounded-lg transition-colors text-sm font-medium ${confirmColor}`} data-testid="confirm-action-btn">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function DeliveryGroup({ title, icon: Icon, messages, accentColor, borderColor, onView, onAction, emptyText, lang, t }) {
  if (!messages || messages.length === 0) {
    return (<Card className={`border-l-4 ${borderColor} opacity-60`}><CardContent className="p-5 flex items-center gap-3"><Icon className={`w-5 h-5 ${accentColor}`} /><p className="text-sm text-stone-400 italic">{emptyText}</p></CardContent></Card>);
  }
  return (
    <Card className={`border-l-4 ${borderColor}`} data-testid={`group-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <CardHeader className="pb-2 pt-4 px-5">
        <div className="flex items-center gap-2"><Icon className={`w-5 h-5 ${accentColor}`} /><CardTitle className="text-sm font-semibold text-stone-700">{title}</CardTitle><Badge variant="secondary" className="ml-auto text-xs">{messages.length}</Badge></div>
      </CardHeader>
      <CardContent className="px-5 pb-4 pt-0">
        <div className="space-y-2">
          {messages.map((msg) => (
            <div key={msg.message_id} className="flex items-center gap-3 p-3 rounded-xl bg-stone-50/80 hover:bg-white hover:shadow-sm border border-transparent hover:border-stone-100 transition-all cursor-pointer group" onClick={() => onView(msg.message_id)} data-testid={`delivery-item-${msg.message_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="font-semibold text-stone-800 text-sm truncate">{msg.title || t("scheduled.noTitle")}</p>
                  <StatusBadge status={msg.status} />
                </div>
                <p className="text-xs text-stone-500">
                  <span className="font-medium">{msg.sender_name}</span><span className="mx-1.5 text-stone-300">&rarr;</span><span>{msg.recipient_name}</span><span className="text-stone-300 mx-1.5">|</span><span>{msg.recipient_email}</span>
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-medium text-stone-600">{formatDateTime(msg.delivery_date, lang)}</p>
                <p className={`text-xs font-semibold ${accentColor}`}>{timeUntil(msg.delivery_date, t, lang)}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-[#E8175D] transition-colors shrink-0" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ScheduledDeliveries({ user }) {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/scheduled-deliveries`, { credentials: "include" });
      if (!res.ok) throw new Error(t("scheduled.accessDenied"));
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (user?.email !== "videogirl25@hotmail.com") { navigate("/"); return; }
    fetchData();
  }, [user, navigate, fetchData]);

  const viewMessage = async (messageId) => {
    const res = await fetch(`${API}/api/admin/messages/${messageId}`, { credentials: "include" });
    if (res.ok) setSelectedMsg(await res.json());
  };

  const handleAction = (action, messageId) => {
    if (action === "delete") {
      setConfirmAction({ action, messageId, title: t("scheduled.deleteConfirmTitle"), description: t("scheduled.deleteConfirmDesc"), confirmLabel: t("common.delete"), confirmColor: "bg-red-500 hover:bg-red-600" });
      return;
    }
    if (action === "force-send") {
      setConfirmAction({ action, messageId, title: t("admin.forceSendConfirmTitle"), description: t("admin.forceSendConfirmDesc"), confirmLabel: t("scheduled.sendNow"), confirmColor: "bg-emerald-500 hover:bg-emerald-600" });
      return;
    }
    executeAction(action, messageId);
  };

  const executeAction = async (action, messageId) => {
    try {
      let res;
      if (action === "pause") res = await fetch(`${API}/api/admin/messages/${messageId}/pause`, { method: "PUT", credentials: "include" });
      else if (action === "force-send") res = await fetch(`${API}/api/admin/messages/${messageId}/force-send`, { method: "POST", credentials: "include" });
      else if (action === "delete") res = await fetch(`${API}/api/admin/messages/${messageId}`, { method: "DELETE", credentials: "include" });
      if (res?.ok) {
        toast.success((await res.json()).message || t("toast.actionDone"));
        setConfirmAction(null);
        fetchData();
      } else { toast.error((await res.json()).detail || t("common.error")); }
    } catch { toast.error(t("toast.networkError")); }
  };

  if (loading) return <div className="min-h-screen bg-[#FDF0F4] flex items-center justify-center"><div className="spinner" /></div>;
  if (error) return (
    <div className="min-h-screen bg-[#FDF0F4] flex items-center justify-center">
      <Card className="max-w-md"><CardContent className="p-8 text-center"><AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" /><p className="text-stone-700 font-medium">{error}</p></CardContent></Card>
    </div>
  );

  const s = data?.summary || {};

  return (
    <div className="min-h-screen bg-[#FDF0F4]" data-testid="scheduled-deliveries-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-stone-500 hover:text-[#E8175D] text-sm mb-2 transition-colors" data-testid="back-to-admin-btn">
              <ArrowLeft className="w-4 h-4" /> {t("scheduled.back")}
            </button>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{t("scheduled.title")}</h1>
          </div>
          <Badge variant="outline" className="border-[#E8175D] text-[#E8175D]">
            {s.total_scheduled || 0} {t("admin.scheduled").toLowerCase()}
          </Badge>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <SummaryCard icon={AlertTriangle} label={t("scheduled.overdue")} count={s.overdue || 0} color="bg-red-50 border-red-200" accent="bg-red-500" />
          <SummaryCard icon={Timer} label={t("scheduled.today")} count={s.today || 0} color="bg-amber-50 border-amber-200" accent="bg-amber-500" />
          <SummaryCard icon={CalendarDays} label={t("scheduled.thisWeek")} count={s.this_week || 0} color="bg-blue-50 border-blue-200" accent="bg-blue-500" />
          <SummaryCard icon={CalendarClock} label={t("scheduled.later")} count={s.later || 0} color="bg-stone-50 border-stone-200" accent="bg-stone-500" />
        </div>

        <div className="space-y-4 mb-8">
          <DeliveryGroup title={t("scheduled.overdue")} icon={AlertTriangle} messages={data?.overdue} accentColor="text-red-500" borderColor="border-red-400" onView={viewMessage} onAction={handleAction} emptyText={t("scheduled.noOverdue")} lang={lang} t={t} />
          <DeliveryGroup title={t("scheduled.today")} icon={Timer} messages={data?.today} accentColor="text-amber-600" borderColor="border-amber-400" onView={viewMessage} onAction={handleAction} emptyText={t("scheduled.noToday")} lang={lang} t={t} />
          <DeliveryGroup title={t("scheduled.thisWeek")} icon={CalendarDays} messages={data?.this_week} accentColor="text-blue-500" borderColor="border-blue-400" onView={viewMessage} onAction={handleAction} emptyText={t("scheduled.noThisWeek")} lang={lang} t={t} />
          <DeliveryGroup title={t("scheduled.later")} icon={CalendarClock} messages={data?.later} accentColor="text-stone-500" borderColor="border-stone-300" onView={viewMessage} onAction={handleAction} emptyText={t("scheduled.noLater")} lang={lang} t={t} />
        </div>

        {data?.recently_sent && data.recently_sent.length > 0 && (
          <Card data-testid="recently-sent-section">
            <CardHeader className="pb-2 pt-4 px-5">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /><CardTitle className="text-sm font-semibold text-stone-700">{t("scheduled.recentlySent")}</CardTitle></div>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              <div className="space-y-2">
                {data.recently_sent.map((msg) => (
                  <div key={msg.message_id} className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
                    <Send className="w-4 h-4 text-emerald-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{msg.title || t("scheduled.noTitle")}</p>
                      <p className="text-xs text-stone-500">{msg.sender_name} &rarr; {msg.recipient_name}</p>
                    </div>
                    <p className="text-xs text-emerald-600 font-medium shrink-0">{formatDateTime(msg.sent_at, lang)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {selectedMsg && <MessageModal message={selectedMsg} onClose={() => setSelectedMsg(null)} onAction={handleAction} t={t} lang={lang} />}
      {confirmAction && (
        <ConfirmModal title={confirmAction.title} description={confirmAction.description} confirmLabel={confirmAction.confirmLabel} confirmColor={confirmAction.confirmColor} onClose={() => setConfirmAction(null)} onConfirm={() => executeAction(confirmAction.action, confirmAction.messageId)} t={t} />
      )}
    </div>
  );
}
